import { css } from "@emotion/css";
import { noOp, RadioButtonNew, RadioGroupNew } from "@octopusdeploy/design-system-components";
import { DebouncedTextWithLabel as DebounceText } from "@octopusdeploy/design-system-octopus-components";
import { borderRadius, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { PolicyBffResource } from "@octopusdeploy/octopus-server-client";
import cn from "classnames";
import React from "react";
import { CodeEditor, TextFormat } from "~/components/CodeEditor/CodeEditor";
import { ExpandableFormSection, required, Summary } from "~/components/form";
import SlugEditor from "~/primitiveComponents/form/Slugs/SlugEditor";
interface PolicySettingsTabProps {
    model: PolicyBffResource;
    onChange: (policy: PolicyBffResource) => void;
    getFieldError: (fieldName: string) => string | undefined;
}
export function PolicySettingsTab({ model, onChange, getFieldError }: PolicySettingsTabProps) {
    const setName = (value: string) => onChange({ ...model, Name: value });
    const setDescription = (value: string) => {
        if (value === "") {
            onChange({ ...model, Description: null });
        }
        else {
            onChange({ ...model, Description: value });
        }
    };
    const setViolationReason = (value: string) => {
        if (value === "") {
            onChange({ ...model, ViolationReason: null });
        }
        else {
            onChange({ ...model, ViolationReason: value });
        }
    };
    const setViolationAction = (value: string) => onChange({ ...model, ViolationAction: value });
    const getViolationActionSummary = () => {
        switch (model.ViolationAction) {
            case "block":
                return Summary.summary("The execution will be stopped from progressing when the policy is non-compliant.");
            case "warn":
                return Summary.summary("The execution will not stop when the policy is non-compliant, but will show a warning.");
            default:
                return Summary.placeholder("Default");
        }
    };
    return (<>
            <ExpandableFormSection errorKey="name" title="Name" focusOnExpandAll summary={model.Name ? Summary.summary(model.Name) : Summary.placeholder("Please enter a name for your policy")} help="A short, memorable, unique name for this policy.">
                <DebounceText value={model.Name} onChange={setName} label="Policy Name" error={getFieldError("Name")} validate={required("Policy name is required.")}/>
                <SlugEditor label="Policy Slug" value={model.Slug} originalSlug={model.Slug} readOnly={true} onChange={noOp}/>
            </ExpandableFormSection>

            <ExpandableFormSection title="Description" errorKey="description" summary={model.Description ? Summary.summary(model.Description) : Summary.placeholder("No policy description provided")} help="Enter a description for this policy.">
                <DebounceText label="Policy description" value={model.Description ?? ""} onChange={setDescription}/>
            </ExpandableFormSection>

            <ExpandableFormSection errorKey="violationReason" title="Violation Reason" summary={model.ViolationReason ? Summary.summary(model.ViolationReason) : Summary.placeholder("No violation reason")} help="The message shown when this policy is non-compliant">
                <DebounceText value={model.ViolationReason ?? ""} onChange={setViolationReason} label="Violation Reason"/>
            </ExpandableFormSection>

            <ExpandableFormSection errorKey="violationAction" title="Violation Action" summary={getViolationActionSummary()} help="The action taken when this policy is non-compliant">
                <RadioGroupNew value={model.ViolationAction} onChange={setViolationAction} label="Voilation Action" hideLabel={true} hideDescription={true} error={getFieldError("violationAction")}>
                    <RadioButtonNew value={"block"} label="Block" name="violationAction" description={"The execution will be stopped from progressing when the policy is non-compliant."} hasDefaultMarker={true}/>
                    <RadioButtonNew value={"warn"} label="Warning" name="violationAction" description={"The execution will not stop when the policy is non-compliant, but will show a warning."}/>
                </RadioGroupNew>
            </ExpandableFormSection>

            <ExpandableFormSection errorKey="scopeRego" title="Scope Rego" summary={Summary.summary("Rego code defining the scope")} help="The Rego code that defines what this policy applies to">
                <RegoCodeEditor value={model.ScopeRego} onChange={(rego) => onChange({ ...model, ScopeRego: rego })} error={getFieldError("scopeRego")}/>
            </ExpandableFormSection>

            <ExpandableFormSection errorKey="conditionsRego" title="Conditions Rego" summary={Summary.summary("Rego code defining the conditions")} help="The Rego code that defines the policy conditions">
                <RegoCodeEditor value={model.ConditionsRego} onChange={(rego) => onChange({ ...model, ConditionsRego: rego })} error={getFieldError("conditionsRego")}/>
            </ExpandableFormSection>
        </>);
}
interface RegoCodeEditorProps {
    value: string;
    onChange: (rego: string) => void;
    error: string | undefined;
}
export function RegoCodeEditor({ value, onChange, error }: RegoCodeEditorProps) {
    return (<div className={cn(error ? styles.error : null)}>
            <CodeEditor value={value} onChange={onChange} language={TextFormat.PlainText} showToolbar={true} showCopyButton={true} allowFullScreen={true}/>
        </div>);
}
const styles = {
    error: css({
        border: `1px solid ${themeTokens.color.border.danger}`,
        borderRadius: borderRadius.medium,
        overflow: "clip",
    }),
};
